import { useState, useEffect } from 'react';

/**
 * Custom hook for tracking credits per semester and period
 * Automatically calculates totals based on selected course IDs
 * @param {Object} groupedCourses - All grouped courses
 * @param {Set} selectedIds - Set of selected course IDs
 * @returns {Object} - Credit tracking state and totals
 */
export const useCreditTracking = (groupedCourses, selectedIds) => {
  const [semesterCredits, setSemesterCredits] = useState({});
  const [periodCredits, setPeriodCredits] = useState({});

  // Calculate credits whenever selectedIds or groupedCourses change
  useEffect(() => {
    const semesterTotals = {};
    const periodTotals = {};

    // Calculate semester and period totals
    Object.entries(groupedCourses).forEach(([key, group]) => {
      const semesterKey = `${group.year}_s${group.semester}`;
      const periodKey = key;

      if (!semesterTotals[semesterKey]) semesterTotals[semesterKey] = 0;
      if (!periodTotals[periodKey]) periodTotals[periodKey] = 0;

      group.courses.forEach(course => {
        const courseId = course.is_split_course 
          ? `${course.semester_instance}_p${course.actual_period}` 
          : course.semester_instance;

        if (selectedIds.has(courseId)) {
          semesterTotals[semesterKey] += course.credits;
          periodTotals[periodKey] += course.credits;
        }
      });
    });

    setSemesterCredits(semesterTotals);
    setPeriodCredits(periodTotals);
  }, [groupedCourses, selectedIds]);

  /**
   * Get total credits across all semesters
   * @returns {number} - Total selected credits
   */
  const getTotalCredits = () => {
    return Object.values(semesterCredits).reduce((sum, credits) => sum + credits, 0);
  };

  /**
   * Get credits for a specific semester
   * @param {string} semesterKey - Semester key (e.g., "2025_s1")
   * @returns {number} - Credits for that semester
   */
  const getSemesterCredits = (semesterKey) => {
    return semesterCredits[semesterKey] || 0;
  };

  /**
   * Get credits for a specific period
   * @param {string} periodKey - Period key (e.g., "2025_s1_p1")
   * @returns {number} - Credits for that period
   */
  const getPeriodCredits = (periodKey) => {
    return periodCredits[periodKey] || 0;
  };

  return {
    semesterCredits,
    periodCredits,
    setSemesterCredits,
    setPeriodCredits,
    getTotalCredits,
    getSemesterCredits,
    getPeriodCredits
  };
}; 